import React, { useState, useRef, useEffect } from 'react';
import { Settings, Coins, Zap, Library, Keyboard, BookOpen, LogOut, User, ChevronDown, AlertTriangle, Clock, Sun, Moon } from 'lucide-react';
import { ModeToggle } from '@features/ModeSystem/components/ModeToggle';
import { LLMProviderToggle } from './LLMProviderToggle';
import { IconButton, Badge } from './ui';
import { version } from '../../package.json';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import type { SunoBilling, SunoAuthStatus } from '@/hooks/useSunoIntegration';

interface HeaderProps {
  sunoBilling: SunoBilling | null;
  sunoAuthStatus?: SunoAuthStatus;
  sunoTokenMinutesRemaining?: number | null;
  onSettingsClick: () => void;
  onLibraryClick: () => void;
  isSettingsOpen: boolean;
  onShortcutRefClick: () => void;
}

export function Header({
  sunoBilling,
  sunoAuthStatus = 'unknown',
  sunoTokenMinutesRemaining = null,
  onSettingsClick,
  onLibraryClick,
  isSettingsOpen,
  onShortcutRefClick
}: HeaderProps): React.ReactElement {
  const showExpired = sunoAuthStatus === 'expired';
  const showExpiringSoon =
    sunoAuthStatus === 'ok' &&
    typeof sunoTokenMinutesRemaining === 'number' &&
    sunoTokenMinutesRemaining > 0 &&
    sunoTokenMinutesRemaining <= 5;
  const { user, isAdmin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <header className="border-b border-slate-700 bg-slate-950/80 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-full mx-auto px-4 sm:px-12 lg:px-16 h-24 flex items-center justify-between gap-4">

        <div className="flex flex-col items-start justify-center h-full">
          <div className="flex items-center gap-3 mb-3">
            <img
              src={theme === 'light' ? '/images/hookhouse-icon-light.png' : '/images/hookhouse-icon-dark.png'}
              alt="HookHouse Pro"
              className="w-10 h-10 rounded-xl shadow-lg shadow-amber-900/20 object-cover"
            />
            <div className="flex flex-col">
              <h1 className="text-3xl font-black tracking-tighter text-slate-50 leading-none">
                HookHouse Pro <span className="text-accent">v{version}</span>
              </h1>
              <div className="flex items-center gap-1.5 mt-1">
                <Badge
                  variant="info-muted"
                  size="xs"
                  leftIcon={<Zap size={10} fill="currentColor" />}
                >
                  AI-Powered Music Architecture
                </Badge>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 font-mono tracking-[0.3em] uppercase pl-1 opacity-80">
            Song Architecture · Vocal Physics · Production Intelligence
          </p>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex flex-col items-center gap-1.5">
            <ModeToggle />
            <LLMProviderToggle />
          </div>

          <div className="h-8 w-[1px] bg-slate-800 hidden sm:block" />

          {showExpired && (
            <button
              onClick={onSettingsClick}
              title="Refresh your Suno bearer token in Settings"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors text-xs font-bold uppercase tracking-wide"
            >
              <AlertTriangle size={12} />
              Suno session expired — refresh
            </button>
          )}
          {showExpiringSoon && (
            <button
              onClick={onSettingsClick}
              title="Suno bearer token expires soon — refresh in Settings"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 transition-colors text-xs font-bold uppercase tracking-wide"
            >
              <Clock size={12} />
              Suno expires in {sunoTokenMinutesRemaining}m
            </button>
          )}
          {sunoBilling && (
            <Badge
              variant="default"
              size="lg"
              pill
              leftIcon={<Coins size={14} className="text-accent" />}
              className="shadow-inner ring-1 ring-slate-700/50 cursor-default"
              title={`${sunoBilling.plan_name} | ${sunoBilling.monthly_usage.toLocaleString()}/${sunoBilling.monthly_limit.toLocaleString()} monthly`}
            >
              <span className="font-bold text-sm tracking-tight">{sunoBilling.credits.toLocaleString()}</span>
              <span className="text-slate-400 font-medium ml-1">Credits</span>
            </Badge>
          )}

          <div className="flex items-center gap-2">
            <IconButton
              variant="ghost"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              className="text-slate-400 hover:text-accent hover:bg-slate-800 hover:scale-105 active:scale-95"
            >
              {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
            </IconButton>
            <IconButton
              variant="ghost"
              onClick={onLibraryClick}
              title="Open Template Library"
              aria-label="Open Template Library"
              className="text-slate-400 hover:text-accent hover:bg-slate-800 hover:scale-105 active:scale-95 [&>svg]:hover:drop-shadow-[0_0_8px_rgba(192, 93, 62,0.4)]"
            >
              <Library size={22} />
            </IconButton>
            <a
              href="/guide"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => localStorage.setItem('hookhouse_guide_opened', 'true')}
              className="p-2 rounded-lg text-slate-400 hover:text-accent hover:bg-slate-800 hover:scale-105 active:scale-95 transition-all duration-200 [&>svg]:hover:drop-shadow-[0_0_8px_rgba(192, 93, 62,0.4)]"
              title="User Guide"
              aria-label="User Guide"
            >
              <BookOpen size={22} />
            </a>
            <IconButton
              variant="ghost"
              onClick={onShortcutRefClick}
              title="Keyboard Shortcuts (Ctrl+/)"
              aria-label="Keyboard Shortcuts"
              className="text-slate-400 hover:text-accent hover:bg-slate-800 hover:scale-105 active:scale-95 [&>svg]:hover:drop-shadow-[0_0_8px_rgba(192, 93, 62,0.4)]"
            >
              <Keyboard size={22} />
            </IconButton>
            <IconButton
              variant="ghost"
              onClick={onSettingsClick}
              title="Settings"
              aria-label="Settings"
              className={
                isSettingsOpen
                  ? 'bg-slate-700 text-accent shadow-lg shadow-black/40 ring-1 ring-accent/20'
                  : 'text-slate-400 hover:text-slate-50 hover:bg-slate-800 hover:scale-105 active:scale-95'
              }
            >
              <Settings size={22} />
            </IconButton>
          </div>

          {user && <UserMenu user={user} isAdmin={isAdmin} logout={logout} />}
        </div>
      </div>
    </header>
  );
}

function UserMenu({ user, isAdmin, logout }: { user: { name: string; email: string }; isAdmin: boolean; logout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative pl-3 border-l border-slate-800 flex items-center gap-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 hover:bg-slate-800 rounded-lg px-2 py-1.5 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center">
          <User size={16} className="text-accent" />
        </div>
        <div className="flex flex-col items-start">
          <span className="text-xs font-semibold text-slate-200 leading-none">{user.name || user.email}</span>
          {isAdmin && (
            <span className="text-[10px] font-bold uppercase tracking-wider mt-0.5 px-1.5 py-0.5 rounded text-accent bg-accent/10 border border-accent/30">
              Admin
            </span>
          )}
        </div>
        <ChevronDown size={14} className={`text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden">
          <div className="p-3 border-b border-slate-800">
            <p className="text-xs text-slate-400 truncate">{user.email}</p>
          </div>
          <div className="p-1.5">
            <button
              onClick={() => { setOpen(false); logout(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <LogOut size={14} />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
